/**
 * Algo Config Parser Utility
 * Parses plain text config files from Algo devices into structured JSON
 */

/**
 * Parse Algo config text into structured sections
 * @param {string} configText - Raw config file content (key=value pairs)
 * @returns {Object} Structured config grouped by section
 * 
 * Example input:
 *   admin.devname = pagingadapter-$MAC$
 *   admin.pwd = algo
 *   audio.page.vol = -18dB
 *   sip.proxy = canalerts.sip.us1.twilio.com
 * 
 * Example output:
 *   {
 *     admin: { devname: "pagingadapter-$MAC$", pwd: "algo" },
 *     audio: { "page.vol": "-18dB" },
 *     sip: { proxy: "canalerts.sip.us1.twilio.com" }
 *   }
 */
export function parseAlgoConfig(configText) {
  if (!configText || typeof configText !== 'string') {
    return {};
  }

  const sections = {};
  const lines = configText.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    
    // Skip empty lines and comments
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith(';')) {
      continue;
    }

    // Parse key=value
    const eqIndex = trimmed.indexOf('=');
    if (eqIndex === -1) {
      continue; // Skip malformed lines
    }

    const fullKey = trimmed.substring(0, eqIndex).trim();
    const value = trimmed.substring(eqIndex + 1).trim();

    // Split by first dot to get section
    const firstDotIndex = fullKey.indexOf('.');
    if (firstDotIndex === -1) {
      // No section, put in 'general'
      if (!sections.general) sections.general = {};
      sections.general[fullKey] = value;
      continue;
    }

    const section = fullKey.substring(0, firstDotIndex);
    const subKey = fullKey.substring(firstDotIndex + 1);

    if (!sections[section]) {
      sections[section] = {};
    }

    sections[section][subKey] = value;
  }

  return sections;
}

/**
 * Convert structured config back to plain text
 * @param {Object} sections - Structured config object
 * @returns {string} Plain text config
 */
export function stringifyAlgoConfig(sections) {
  const lines = [];

  for (const [section, params] of Object.entries(sections)) {
    for (const [key, value] of Object.entries(params)) {
      const fullKey = section === 'general' ? key : `${section}.${key}`;
      lines.push(`${fullKey} = ${value}`);
    }
  }

  return lines.join('\n');
}

/**
 * Get full parameter key from section and subkey
 * @param {string} section - Section name (e.g., 'audio')
 * @param {string} subKey - Sub-key (e.g., 'page.vol')
 * @returns {string} Full key (e.g., 'audio.page.vol')
 */
export function getFullKey(section, subKey) {
  return section === 'general' ? subKey : `${section}.${subKey}`;
}

/**
 * Detect if value should be a select dropdown
 * @param {string} value - Current value
 * @returns {boolean} True if should use select
 */
export function shouldUseSelect(value) {
  if (!value) return false;
  
  const lowerValue = value.toLowerCase();
  const selectValues = ['default', 'none', 'auto', 'yes', 'no', '0', '1', 'true', 'false', 'on', 'off', 'enable', 'disable'];
  
  return selectValues.includes(lowerValue);
}

/**
 * Get common options for select dropdowns
 * @param {string} currentValue - Current value
 * @returns {Array<string>} Array of options
 */
export function getSelectOptions(currentValue) {
  const lowerValue = currentValue?.toLowerCase() || '';
  
  // Boolean-like values
  if (['yes', 'no', '0', '1', 'true', 'false', 'on', 'off', 'enable', 'disable'].includes(lowerValue)) {
    if (['yes', 'no'].includes(lowerValue)) return ['yes', 'no'];
    if (['0', '1'].includes(lowerValue)) return ['0', '1'];
    if (['true', 'false'].includes(lowerValue)) return ['true', 'false'];
    if (['on', 'off'].includes(lowerValue)) return ['on', 'off'];
    if (['enable', 'disable'].includes(lowerValue)) return ['enable', 'disable'];
  }
  
  // Special values
  if (['default', 'none', 'auto'].includes(lowerValue)) {
    return ['default', 'none', 'auto'];
  }
  
  // Fallback
  return [currentValue, 'default', 'none', 'auto'];
}

/**
 * Filter sections by search query
 * @param {Object} sections - Structured config
 * @param {string} query - Search query
 * @returns {Object} Filtered sections
 */
export function filterSections(sections, query) {
  if (!query || !query.trim()) {
    return sections;
  }

  const lowerQuery = query.toLowerCase().trim();
  const filtered = {};

  for (const [section, params] of Object.entries(sections)) {
    const filteredParams = {};
    
    for (const [key, value] of Object.entries(params)) {
      const fullKey = getFullKey(section, key);
      const matchesKey = fullKey.toLowerCase().includes(lowerQuery);
      const matchesValue = value?.toString().toLowerCase().includes(lowerQuery);
      
      if (matchesKey || matchesValue) {
        filteredParams[key] = value;
      }
    }

    if (Object.keys(filteredParams).length > 0) {
      filtered[section] = filteredParams;
    }
  }

  return filtered;
}

/**
 * Get section display name
 * @param {string} section - Section key
 * @returns {string} Display name
 */
export function getSectionDisplayName(section) {
  const names = {
    admin: 'Administration',
    audio: 'Audio Settings',
    sip: 'SIP Configuration',
    net: 'Network Settings',
    mcast: 'Multicast',
    ntp: 'Time & NTP',
    syslog: 'System Logging',
    snmp: 'SNMP',
    relay: 'Relay Control',
    input: 'Input Settings',
    output: 'Output Settings',
    schedule: 'Scheduling',
    general: 'General Settings'
  };
  
  return names[section] || section.charAt(0).toUpperCase() + section.slice(1);
}
